import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FaBullseye,
  FaEye,
  FaHandshake,
  FaUsers,
  FaBalanceScale,
  FaRegCheckCircle,
  FaSpinner,
} from "react-icons/fa";
import ScrollToTop from "../scrollToTop/ScrollToTop";

const iconMap = {
  integrity: <FaBalanceScale />,
  excellence: <FaRegCheckCircle />,
  inclusiveness: <FaUsers />,
  collaboration: <FaHandshake />,
  serving: <FaUsers />,
  transparency: <FaEye />,
};

const parseCoreValues = (coreValues) => {
  try {
    // If it's already an array, return it
    if (Array.isArray(coreValues)) return coreValues;
    
    // If it's a string, try to parse it
    if (typeof coreValues === 'string') {
      // Handle case where it might be double-encoded
      let parsed = coreValues;
      try {
        parsed = JSON.parse(coreValues);
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }
      } catch (e) {
        console.log("Couldn't parse core_values as JSON, treating as string");
      }
      
      // If we got an array, return it
      if (Array.isArray(parsed)) return parsed;
      
      // If we got a string, split by commas or newlines
      if (typeof parsed === 'string') {
        return parsed.split(/[\n,]/).filter(val => val.trim() !== "");
      }
    }
    
    // Fallback to empty array
    return [""];
  } catch (e) {
    console.error("Error parsing core values:", e);
    return [""];
  }
};

const About = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/about`);
        const data = res.data.data;
        if (data) {
          // Set default values if fields are empty
          const parsedCoreValues = parseCoreValues(data.core_values);

          const processedData = {
            introduction: data.introduction || "No introduction available",
            mission: data.mission || "No mission statement available",
            vision: data.vision || "No vision statement available",
            purpose: data.purpose || "No purpose statement available",
            core_values: parsedCoreValues.length > 0 ? parsedCoreValues : [""],
          };
          setAbout(processedData);
        } else {
          // Set default about data if no data from API
          setAbout({
            introduction: "No introduction available",
            mission: "No mission statement available",
            vision: "No vision statement available",
            purpose: "No purpose statement available",
            core_values: []
          });
        }
      } catch (err) {
        console.error("Error fetching About info", err);
        // Set default about data on error
        setAbout({
          introduction: "No introduction available",
          mission: "No mission statement available",
          vision: "No vision statement available",
          purpose: "No purpose statement available",
          core_values: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAbout();
  }, []);
  const renderFormattedContent = (html) => {
    return (
      <div className="ql-editor" 
           style={{
             padding: 0,
             fontSize: '1rem',
             lineHeight: '1.5',
             color: '#4b5563'
           }}>
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  };
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white dark:bg-gray-900">
        <div className="flex items-center gap-4 p-4 rounded-lg shadow-md bg-gray-100 dark:bg-gray-800">
          <FaSpinner className="animate-spin text-4xl text-blue-600" />
          <h2 className="text-xl font-semibold text-blue-600 dark:text-blue-400">
            Bishoftu Finance Office
          </h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <div className="bg-gray-50 text-gray-800 mt-10">
        {/* Hero Section */}
        <section className="bg-blue-100 py-20 text-center px-4">
          <motion.h2
            className="text-xl md:text-3xl lg:text-4xl font-bold text-blue-800 mb-4"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            About Bishoftu Finance Office
          </motion.h2>
        </section>

        {/* Mission & Vision */}
        <section className="max-w-6xl mx-auto py-16 px-4 grid md:grid-cols-2 gap-12">
          <motion.p
            className="text-lg max-w-2xl mx-auto text-gray-700"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {renderFormattedContent(about.introduction)}
          </motion.p>
          <div className="gap-20 m-4 space-y-28">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="flex items-center text-2xl font-bold text-blue-700 mb-4">
                <FaBullseye className="mr-2" /> Mission
              </h3>
              <p className="text-gray-700 leading-relaxed" > {renderFormattedContent(about.mission)}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="flex items-center text-2xl font-bold text-blue-700 mb-4">
                <FaEye className="mr-2" /> Vision
              </h3>
              <p className="text-gray-700 leading-relaxed"> {renderFormattedContent(about.vision)}</p>
            </motion.div>
          </div>
        </section>

        {/* Core Values */}
        <section className="bg-white py-16 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <motion.h3
              className="text-3xl font-bold text-blue-800 mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              Core Values
            </motion.h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {about.core_values.map((value, index) => {
                const lowerValue = value.toLowerCase();
                const icon = iconMap[lowerValue] || 
                  <FaRegCheckCircle className="text-emerald-600" />;
                
                return (
                  <motion.div
                    key={index}
                    className="flex items-center p-4 rounded-lg bg-gray-50 hover:bg-emerald-50 transition-colors"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="text-2xl mr-3">
                      {icon}
                    </div>
                    <span className="font-medium text-gray-800">{value}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Purpose */}
        <section className="bg-blue-50 py-16 px-4 text-center">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-blue-800 mb-6">Our Purpose</h3>
            <p className="text-lg text-gray-700"> {renderFormattedContent(about.purpose)}</p>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default About;